import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Box } from '@chakra-ui/react';
import { DisplayChakraProvider } from './context/DisplaySettingsContext';
import { lazy, Suspense } from 'react';
import { WebSocketProvider } from './context/WebSocketContext';
import { GuestRequestsProvider } from './context/GuestRequestsContext';
import { MessagesProvider } from './context/MessagesContext';
import { DoorLockProvider } from './context/DoorLockContext';
import apiClient from './service/ApiClient.js';

//import HotelApp from './components/HotelManagementSystem';
import Login from './components/Login.jsx';
import MagicLogin from './components/auth/MagicLogin.jsx';
import SmartRedirect from './components/SmartRedirect.jsx';
import ErrorBoundary from './components/common/ErrorBoundary.jsx';
import DemoBanner from './components/common/DemoBanner.jsx';
import CommunicationsHoldBanner from './components/common/CommunicationsHoldBanner.jsx';
import HotelLabelOverrides from './components/common/HotelLabelOverrides.jsx';
import ShiftReportPrintPage from './components/ShiftReportPrintPage.jsx';
import { REFERRALS_ENABLED } from './config/featureFlags';
import { isDemoMode, DEMO_USER, DEMO_PASSWORD } from './config/demoMode';

const HotelApp = lazy(() => import('./components/HotelManagementSystem.js'));
// Rarely visited: keep it out of the login-critical bundle.
const ReferralsPage = lazy(() => import('./components/settings/ReferralsPage.jsx'));
const ConnectAi = lazy(() => import('./components/ConnectAi.jsx'));
// Protected Route: token check only. This used to await an "needs
// onboarding" probe (current-user + hotel setup status, two API calls)
// and show "Loading..." until both answered — on every protected route
// mount, with the answer never used. The onboarding wizard no longer
// lives in this app, so the app renders as soon as the token is present.
const ProtectedRoute = ({ children }) => {
  if (!apiClient.isAuthenticated()) {
    return <Navigate to="/login" />;
  }
  return children;
};

function App() {
  const [demoReady, setDemoReady] = useState(!isDemoMode());
  const [demoError, setDemoError] = useState(null);

  // Auto-login for demo mode at app boot. Skipped entirely for normal users.
  useEffect(() => {
    if (!isDemoMode()) return;
    if (apiClient.isAuthenticated()) {
      setDemoReady(true);
      return;
    }
    (async () => {
      try {
        await apiClient.login(DEMO_USER, DEMO_PASSWORD);
        setDemoReady(true);
      } catch (err) {
        console.error('Demo auto-login failed:', err);
        setDemoError(
          'Demo login failed. Please try refreshing the page.'
        );
        setDemoReady(true); // unblock render so error is visible
      }
    })();
  }, []);

  if (!demoReady) {
    return (
      <DisplayChakraProvider>
        <Box minH="100vh" display="flex" alignItems="center" justifyContent="center">
          <Box textAlign="center" color="gray.500" fontSize="sm">
            Loading demo…
          </Box>
        </Box>
      </DisplayChakraProvider>
    );
  }

  return (
    <DisplayChakraProvider>
      <ErrorBoundary>
        {/* Demo mode persistent banner — only shown when isDemoMode() is true */}
        {isDemoMode() && !demoError && <DemoBanner />}
        {isDemoMode() && demoError && (
          <Box bg="red.500" color="white" px={4} py={2} fontSize="sm" textAlign="center">
            {demoError}
          </Box>
        )}
        {/* Communications hold — checks itself, renders nothing unless on */}
        <CommunicationsHoldBanner />
        {/* <HotelSystemProvider> */}
          <Router>
            {/* Per-hotel label renames (admine → Hotel Settings → Labels) */}
            <HotelLabelOverrides />
            <Suspense fallback={null}>
            <Routes>
            <Route path="/login" element={<Login />} />
            {/* One-click auto-signin link emailed to trial prospects on
                signup — must be public/unauthenticated, a logged-out
                prospect needs to reach it. See MagicLogin.jsx. */}
            <Route path="/auth/magic-login" element={<MagicLogin />} />
            {/* OAuth consent for AI connectors (claude.ai, ChatGPT, Claude
                Code). Handles its own sign-in redirect with ?next= so the
                signed request survives login. See ConnectAi.jsx. */}
            <Route path="/connect-ai" element={<ConnectAi />} />
            {/* Settings — Referrals standalone page (gated by feature flag) */}
            {REFERRALS_ENABLED && (
              <Route
                path="/admin/settings/referrals"
                element={
                  <ProtectedRoute>
                    <Box minH="100vh" bg="gray.50" py={8}>
                      <ReferralsPage />
                    </Box>
                  </ProtectedRoute>
                }
              />
            )}

            {/* Cash-counter shift slip, full page so window.print() gets the
                whole document (printing from inside a scrolling modal clips). */}
            <Route
              path="/print/shift/:id"
              element={
                <ProtectedRoute>
                  <ShiftReportPrintPage />
                </ProtectedRoute>
              }
            />

            {/* Default routes */}
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <WebSocketProvider
                    hotelId={apiClient.getHotelId()}
                    authToken={apiClient.getToken()}
                  >
                    <GuestRequestsProvider>
                      <MessagesProvider>
                        <DoorLockProvider>
                          <HotelApp />
                        </DoorLockProvider>
                      </MessagesProvider>
                    </GuestRequestsProvider>
                  </WebSocketProvider>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/" 
              element={
                <ProtectedRoute>
                  <SmartRedirect />
                </ProtectedRoute>
              } 
            />
            <Route path="*" element={<Navigate to="/" />} />
            </Routes>
            </Suspense>
          </Router>
        {/* </HotelSystemProvider> */}
      </ErrorBoundary>
    </DisplayChakraProvider>
  );
}

export default App;